import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Users, Globe, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <div className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-primary-600" />
              <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Last Updated */}
          <div className="mb-8 p-4 bg-primary-50 rounded-lg">
            <p className="text-sm text-primary-700">
              <strong>Last Updated:</strong> December 2024
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-6 h-6 text-primary-600 mr-2" />
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Bulk Leads Caller ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our mobile application.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Please read this Privacy Policy carefully. If you do not agree with the terms of 
              this Privacy Policy, please do not access the application.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Database className="w-6 h-6 text-primary-600 mr-2" />
              Information We Collect
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Information</h3>
                <p className="text-gray-700 leading-relaxed">
                  When you upload Excel files or manually enter contact information, we collect 
                  the contact details you provide, including names, phone numbers, and any 
                  additional information you choose to include.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Call Data</h3>
                <p className="text-gray-700 leading-relaxed">
                  We collect information about your calling activities, including call duration, 
                  call outcomes, timestamps, and any notes or remarks you add to contacts.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Device Information</h3>
                <p className="text-gray-700 leading-relaxed">
                  We may collect device-specific information such as your device model, 
                  operating system version, unique device identifiers, and mobile network information.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Usage Analytics</h3>
                <p className="text-gray-700 leading-relaxed">
                  We collect information about how you use the app, including features used, 
                  session duration, and app performance data to improve our services.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="w-6 h-6 text-primary-600 mr-2" />
              How We Use Your Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">Core Functionality</h3>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Process and manage your contact lists</li>
                    <li>• Enable calling functionality</li>
                    <li>• Track call outcomes and progress</li>
                    <li>• Generate reports and analytics</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">App Improvement</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Analyze usage patterns</li>
                    <li>• Identify and fix bugs</li>
                    <li>• Enhance user experience</li>
                    <li>• Develop new features</li>
                  </ul>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-semibold text-purple-800 mb-2">Support & Communication</h3>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• Provide customer support</li>
                    <li>• Respond to inquiries</li>
                    <li>• Send important updates</li>
                    <li>• Notify about app changes</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h3 className="font-semibold text-orange-800 mb-2">Legal Compliance</h3>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• Comply with legal obligations</li>
                    <li>• Protect against fraud</li>
                    <li>• Enforce our terms of service</li>
                    <li>• Respond to legal requests</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Data Storage & Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="w-6 h-6 text-primary-600 mr-2" />
              Data Storage & Security
            </h2>
            
            <div className="space-y-4">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Local Storage</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>All your data is stored locally on your device.</strong> We do not 
                  transmit your contact information or call data to our servers. This ensures 
                  maximum privacy and security for your sensitive business information.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Contact lists remain on your device</li>
                  <li>• Call history is stored locally</li>
                  <li>• Reports are generated on-device</li>
                  <li>• No cloud synchronization required</li>
                </ul>
              </div>

              <div className="p-6 bg-green-50 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-3">Security Measures</h3>
                <ul className="text-sm text-green-700 space-y-2">
                  <li>• <strong>Encryption:</strong> Data is encrypted using industry-standard encryption methods</li>
                  <li>• <strong>Access Control:</strong> Only you have access to your data</li>
                  <li>• <strong>Secure Storage:</strong> Data is stored in secure, sandboxed app directories</li>
                  <li>• <strong>No Network Transmission:</strong> Your data never leaves your device</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Users className="w-6 h-6 text-primary-600 mr-2" />
              Information Sharing
            </h2>
            
            <div className="p-6 bg-red-50 rounded-lg">
              <h3 className="text-lg font-semibold text-red-800 mb-3">We Do NOT Share Your Data</h3>
              <p className="text-red-700 leading-relaxed mb-4">
                <strong>We do not sell, trade, or otherwise transfer your personal information 
                to third parties.</strong> Since all data is stored locally on your device, 
                there is no data to share with external parties.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  <p className="text-sm text-red-700">
                    <strong>No Third-Party Access:</strong> Your contact lists and call data 
                    are not accessible to any external services or companies.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  <p className="text-sm text-red-700">
                    <strong>No Data Mining:</strong> We do not analyze your personal data 
                    for marketing or advertising purposes.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  <p className="text-sm text-red-700">
                    <strong>No Cloud Storage:</strong> Your data is never uploaded to cloud 
                    servers or external databases.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Globe className="w-6 h-6 text-primary-600 mr-2" />
              Your Rights & Choices
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">Data Control</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Delete contacts anytime</li>
                    <li>• Clear call history</li>
                    <li>• Export your data</li>
                    <li>• Uninstall app to remove all data</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">Privacy Settings</h3>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Control app permissions</li>
                    <li>• Manage storage settings</li>
                    <li>• Disable analytics (if any)</li>
                    <li>• Customize data retention</li>
                  </ul>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-semibold text-purple-800 mb-2">Access Rights</h3>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• View all stored data</li>
                    <li>• Request data export</li>
                    <li>• Correct inaccurate data</li>
                    <li>• Request data deletion</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h3 className="font-semibold text-orange-800 mb-2">Contact Us</h3>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• Email: privacy@bulkleadscaller.com</li>
                    <li>• Support: support@bulkleadscaller.com</li>
                    <li>• Response time: 48 hours</li>
                    <li>• Free assistance provided</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <div className="p-6 bg-yellow-50 rounded-lg">
              <p className="text-yellow-800 leading-relaxed">
                <strong>Our app is not intended for children under 13 years of age.</strong> 
                We do not knowingly collect personal information from children under 13. 
                If you are a parent or guardian and believe your child has provided us with 
                personal information, please contact us immediately.
              </p>
            </div>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <div className="p-6 bg-gray-50 rounded-lg">
              <p className="text-gray-700 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. We will notify you of 
                any changes by posting the new Privacy Policy on this page and updating the 
                "Last Updated" date.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You are advised to review this Privacy Policy periodically for any changes. 
                Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <div className="p-6 bg-primary-50 rounded-lg">
              <p className="text-primary-800 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, 
                please contact us:
              </p>
              <div className="space-y-2 text-primary-700">
                <p><strong>Email:</strong> privacy@bulkleadscaller.com</p>
                <p><strong>Support:</strong> support@bulkleadscaller.com</p>
                <p><strong>Response Time:</strong> Within 48 hours</p>
                <p><strong>Data Protection Officer:</strong> Available upon request</p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-8 mt-8">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">
                This Privacy Policy is effective as of December 2024
              </p>
              <p className="text-xs text-gray-400">
                Bulk Leads Caller - Professional Lead Management App
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
